import "./index.css";
import { useTranslation } from "react-i18next";
import { Button, Card, CardContent, CardHeader, Toast } from "@/ui-kit";
import { InputPasswordControl } from "@/components/controls";
import { FormProvider, useForm } from "react-hook-form";
import { useCallback, useMemo, useState } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { UsersRepository } from "@/api/users/repository";
import { UsersService } from "@/api/users/service";
import { serverErrorHandler } from "@/utils/serverErrorHandler";

type IFormData = {
  old_password: string;
  new_password: string;
  repeat_password: string;
};

export const PasswordSettings = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const repository = useMemo(
    () => new UsersRepository(new UsersService(), { abort: true }),
    [],
  );

  const schema = useMemo(() => {
    return z
      .object({
        old_password: z.string().min(1, { message: t("validation.required") }),
        new_password: z
          .string()
          .min(1, { message: t("validation.required") })
          .min(8, { message: t("validation.password_length") }),
        repeat_password: z
          .string()
          .min(1, { message: t("validation.required") })
          .min(8, { message: t("validation.password_length") }),
      })
      .refine((data) => data.new_password === data.repeat_password, {
        message: t("validation.passwords_must_match"),
        path: ["repeat_password"],
      });
  }, [t]);

  const form = useForm<IFormData>({
    defaultValues: {
      old_password: "",
      new_password: "",
      repeat_password: "",
    },
    resolver: zodResolver(schema),
  });

  const { handleSubmit, reset } = form;

  const onSubmit = useCallback(
    (formData: IFormData) => {
      setLoading(true);
      repository
        .updatePassword({
          oldPassword: formData.old_password,
          newPassword: formData.new_password,
        })
        .then(() => {
          Toast.success({ title: t("toast.password_updated_success") });
          reset();
        })
        .catch(
          serverErrorHandler(() => {
            setLoading(false);
          }),
        )
        .finally(() => {
          setLoading(false);
        });
    },
    [repository, reset, t],
  );

  return (
    <Card variant="layer1">
      <CardHeader title={t("action.changePassword")} />
      <CardContent>
        <FormProvider {...form}>
          <form
            className="password-settings-form"
            noValidate
            onSubmit={handleSubmit(onSubmit)}
          >
            <InputPasswordControl
              label={t("label.old_password")}
              name="old_password"
              color="layer2"
              autoComplete="off"
            />

            <InputPasswordControl
              label={t("label.new_password")}
              name="new_password"
              color="layer2"
              autoComplete="off"
            />

            <InputPasswordControl
              label={t("label.repeat_password")}
              name="repeat_password"
              color="layer2"
              autoComplete="off"
            />

            <div className="password-settings-form__actions">
              <Button
                type="submit"
                content={t("action.changePassword")}
                loading={loading}
              />
            </div>
          </form>
        </FormProvider>
      </CardContent>
    </Card>
  );
};
